import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import PageHeader from '../../../PageHeader/PageHeader';
import { HitApi } from '../../../Api/ApiHit';
import { searchDevice, searchMedia, incrementView } from '../../../constant/Constant';
import { getSelectedUnit } from '../../../storage/Storage';
import toast from 'react-hot-toast';
import { ArrowLeft, Eye } from 'lucide-react';

function DeviceAds() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [device, setDevice] = useState(null);
    const [deviceAds, setDeviceAds] = useState([]);
    const [loadingDevice, setLoadingDevice] = useState(true);
    const [loadingAds, setLoadingAds] = useState(true);

    useEffect(() => {
        if (id) {
            fetchDeviceDetails();
            fetchDeviceAds();
        }
    }, [id]);

    const fetchDeviceDetails = () => {
        setLoadingDevice(true);
        const json = {
            page: 1,
            limit: 1,
            search: {
                _id: id,
                unitIds: getSelectedUnit()?._id
            }
        };

        HitApi(json, searchDevice).then((res) => {
            if (res?.statusCode === 200) {
                const deviceData = res?.data?.docs?.[0];
                if (deviceData) {
                    setDevice(deviceData);
                } else {
                    toast.error('Device not found');
                    navigate('/registerdevice');
                }
            } else {
                toast.error('Failed to fetch device details');
                navigate('/registerdevice');
            }
        }).catch(error => {
            console.error('Error fetching device:', error);
            toast.error('Error fetching device details');
            navigate('/registerdevice');
        }).finally(() => {
            setLoadingDevice(false);
        });
    };

    const fetchDeviceAds = () => {
        setLoadingAds(true);
        const json = {
            page: 1,
            limit: 100,
            search: {
                deviceId: id
            }
        };

        HitApi(json, searchMedia).then((res) => {
            if (res?.statusCode === 200) {
                const adsData = Array.isArray(res?.data?.docs) ? res?.data?.docs : [];
                setDeviceAds(adsData);
            } else {
                setDeviceAds([]);
                toast.error('Failed to fetch advertisements.');
            }
        }).catch(error => {
            console.error('Error fetching ads:', error);
            setDeviceAds([]);
            toast.error('Error fetching advertisements.');
        }).finally(() => {
            setLoadingAds(false);
        });
    };

    const handleBack = () => {
        navigate('/registerdevice');
    };

    const handleIncrementView = (adId) => {
        const json = {
            _id: adId,
            deviceId: id
        };

        HitApi(json, incrementView).then((res) => {
            if (res?.statusCode === 200) {
                toast.success(`View tracked! Unique views: ${res.data.uniqueViewCount}`);
                // Refresh the ads list to show updated count
                fetchDeviceAds();
            } else {
                toast.error('Failed to increment view count');
            }
        }).catch(error => {
            console.error('Error incrementing view:', error);
            toast.error('Error incrementing view count');
        });
    };

    if (loadingDevice) {
        return (
            <div className="p-5">
                <div className="flex justify-center items-center h-64">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
                </div>
            </div>
        );
    }

    return (
        <div className="p-5">
            <PageHeader
                title="Device Advertisements"
                description={device ? `View all advertisements for ${device.deviceCode} - ${device.deviceName}` : 'Loading...'}
            />

            {/* Back Button */}
            <div className="mb-4">
                <button
                    onClick={handleBack}
                    className="flex items-center space-x-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-colors"
                >
                    <ArrowLeft size={18} />
                    <span>Back to Devices</span>
                </button>
            </div>

            {/* Device Info Card */}
            {device && (
                <div className="bg-white rounded-lg shadow-md p-6 mb-6 border border-gray-200">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Device Details</h3>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                            <p className="text-sm text-gray-600">Device Code</p>
                            <p className="text-base font-medium text-gray-900">{device.deviceCode}</p>
                        </div>
                        <div>
                            <p className="text-sm text-gray-600">Device Name</p>
                            <p className="text-base font-medium text-gray-900">{device.deviceName}</p>
                        </div>
                        <div>
                            <p className="text-sm text-gray-600">Device Type</p>
                            <p className="text-base font-medium text-gray-900">{device.deviceType}</p>
                        </div>
                        <div>
                            <p className="text-sm text-gray-600">Orientation</p>
                            <p className="text-base font-medium text-gray-900">
                                {device.orientation === 'vertical' ? 'Vertical' : 'Horizontal'}
                            </p>
                        </div>
                        <div>
                            <p className="text-sm text-gray-600">Resolution</p>
                            <p className="text-base font-medium text-gray-900">{device.resolution || 'N/A'}</p>
                        </div>
                        <div>
                            <p className="text-sm text-gray-600">Status</p>
                            <span className={`inline-block px-2 py-1 rounded-full text-xs font-medium ${device.status === 'Active' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                                }`}>
                                {device.status}
                            </span>
                        </div>
                    </div>
                </div>
            )}

            {/* Advertisements Grid */}
            <div className="bg-white rounded-lg shadow-md p-6 border border-gray-200">
                <div className="flex justify-between items-center mb-4">
                    <h3 className="text-lg font-semibold text-gray-900">
                        Advertisements ({deviceAds.length})
                    </h3>
                </div>

                {loadingAds ? (
                    <div className="flex justify-center items-center py-12">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
                    </div>
                ) : deviceAds.length === 0 ? (
                    <div className="text-center py-12">
                        <p className="text-gray-500">No advertisements found for this device.</p>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {deviceAds.map((ad, index) => (
                            <div key={ad._id || index} className="border rounded-lg overflow-hidden shadow-sm hover:shadow-md transition-shadow">
                                {/* Media Preview */}
                                <div className="bg-gray-100 h-48 flex items-center justify-center">
                                    {ad.type === 'image' || ad.type === 'gif' ? (
                                        <img
                                            src={ad.source}
                                            alt={`Ad ${index + 1}`}
                                            className="w-full h-full object-cover"
                                            onError={(e) => {
                                                e.target.onerror = null;
                                                e.target.src = '/placeholder-image.png';
                                            }}
                                        />
                                    ) : ad.type === 'video' ? (
                                        <video
                                            src={ad.source}
                                            className="w-full h-full object-cover"
                                            controls
                                        />
                                    ) : (
                                        <span className="text-gray-400">No preview</span>
                                    )}
                                </div>

                                {/* Ad Details */}
                                <div className="p-4 bg-white">
                                    <div className="flex justify-between items-start mb-2">
                                        <div className="flex items-center space-x-2">
                                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${ad.type === 'image' ? 'bg-blue-100 text-blue-800' :
                                                    ad.type === 'video' ? 'bg-purple-100 text-purple-800' :
                                                        'bg-green-100 text-green-800'
                                                }`}>
                                                {ad.type?.toUpperCase()}
                                            </span>
                                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${ad.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                                                }`}>
                                                {ad.isActive ? 'Active' : 'Inactive'}
                                            </span>
                                        </div>
                                        <button
                                            onClick={() => handleIncrementView(ad._id)}
                                            className="flex items-center space-x-1 px-2 py-1 bg-indigo-500 hover:bg-indigo-600 text-white rounded text-xs font-medium transition-colors"
                                            title="Simulate View"
                                        >
                                            <Eye size={14} />
                                            <span>+1</span>
                                        </button>
                                    </div>
                                    <div className="space-y-2 text-sm text-gray-600">
                                        <p className="flex items-center justify-between">
                                            <span className="font-medium">Duration:</span>
                                            <span className="px-2 py-1 bg-orange-100 rounded text-orange-800 font-medium">
                                                {ad.duration}s
                                            </span>
                                        </p>
                                        <p className="flex items-center justify-between">
                                            <span className="font-medium">Views:</span>
                                            <span className="px-3 py-1 bg-indigo-100 rounded-full text-indigo-800 font-semibold">
                                                {ad.uniqueViewCount || 0}
                                            </span>
                                        </p>
                                        {ad.lastViewedAt && (
                                            <p className="flex items-center justify-between">
                                                <span className="font-medium text-xs">Last Viewed:</span>
                                                <span className="text-xs text-gray-500">
                                                    {new Date(ad.lastViewedAt).toLocaleDateString('en-US', {
                                                        month: 'short',
                                                        day: 'numeric',
                                                        hour: '2-digit',
                                                        minute: '2-digit'
                                                    })}
                                                </span>
                                            </p>
                                        )}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}

export default DeviceAds;
